import { Box, styled } from '@mui/material';
import type { ReactNode } from 'react';
import type { DataStatus } from '@/shared/lib';
import { isError, isIdle, isLoading } from '@/shared/lib';
import { Loader } from '@/shared/ui';
import { ErrorIcon } from './ErrorIcon';
import { InfoIcon } from './InfoIcon';

const Panel = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  padding: `${theme.spacing(5)} ${theme.spacing(3)}`,
  borderRadius: theme.spacing(1),
  backgroundColor: '#404040',
  color: 'white',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  alignItems: 'center',
  textAlign: 'center',
  width: 300,
}));

interface Props {
  dataStatus: DataStatus;
  noData: boolean;
  noSelection: boolean;
  keepContentWhileLoading?: boolean;
  noDataMessage?: ReactNode;
  children: ReactNode;
}

export function LineChartWrapper({
  dataStatus,
  noData,
  noSelection,
  keepContentWhileLoading = false,
  noDataMessage = 'There is no data for selected axes and filters',
  children,
}: Props) {
  if (isLoading(dataStatus) || isIdle(dataStatus)) {
    if (!keepContentWhileLoading) {
      return <Loader active />;
    }

    return (
      <>
        {children}
        <Loader active />
      </>
    );
  }

  if (isError(dataStatus)) {
    return (
      <Panel>
        <ErrorIcon />
        Something happened and we could not retrieve any data. Please, try again
        later
      </Panel>
    );
  }

  if (noData) {
    return (
      <Panel>
        <InfoIcon />
        {noDataMessage}
      </Panel>
    );
  }

  if (noSelection) {
    return (
      <Panel>
        <InfoIcon />
        Nothing selected
      </Panel>
    );
  }

  return children;
}
